import { useEffect, useState } from "react";
import { Tooltip } from "antd";
import { CloseCircleOutlined } from "@ant-design/icons";
import { ColorFieldProps } from "./types";
import { DEFAULT_EMPTY_COLOR } from "./constants";
import {
  FieldRow,
  SwatchGroup,
  Swatch,
  SwatchEmpty,
  FieldMeta,
  FieldLabel,
  FieldDescription,
  HexInput,
  ClearBtn,
} from "./styles";

// Accepts with or without a leading '#', 3/6/8 hex digits (the last two
// forms match what <input type="color"> and the backend's IsHexColor both
// expect once normalized).
const HEX_PATTERN = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

const normalizeHex = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (!HEX_PATTERN.test(trimmed)) return null;
  return `#${trimmed.replace(/^#/, "").toLowerCase()}`;
};

function ColorField({ fieldKey, label, description, value, onChange, onClear }: ColorFieldProps) {
  const isSet = value !== null;
  const [draft, setDraft] = useState(value ?? "");
  const [invalid, setInvalid] = useState(false);

  // Stay in sync when the value changes from elsewhere (swatch picker,
  // Clear button, or a fresh fetch) rather than from this input itself.
  useEffect(() => {
    setDraft(value ?? "");
    setInvalid(false);
  }, [value]);

  const commit = () => {
    const raw = draft.trim();
    if (raw === "") {
      setInvalid(false);
      if (isSet) onClear(fieldKey);
      return;
    }
    const normalized = normalizeHex(raw);
    if (normalized) {
      setDraft(normalized);
      setInvalid(false);
      if (normalized !== value) onChange(fieldKey, normalized);
    } else {
      setInvalid(true);
    }
  };

  return (
    <FieldRow>
      <SwatchGroup>
        <Tooltip title={isSet ? `Click to pick ${label}` : `Click to pick ${label}`}>
          <Swatch $color={value} $isSet={isSet} htmlFor={`color-${fieldKey}`}>
            {!isSet && <SwatchEmpty>+</SwatchEmpty>}
            <input
              id={`color-${fieldKey}`}
              type="color"
              value={isSet ? value : DEFAULT_EMPTY_COLOR}
              onChange={(e) => onChange(fieldKey, e.target.value)}
            />
          </Swatch>
        </Tooltip>

        <Tooltip title={invalid ? "Enter a valid hex color, e.g. #984343" : "Or type a hex code here"} open={invalid || undefined}>
          <HexInput
            $invalid={invalid}
            value={draft}
            placeholder="#000000"
            maxLength={9}
            spellCheck={false}
            onChange={(e) => {
              setDraft(e.target.value);
              if (invalid) setInvalid(false);
            }}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              } else if (e.key === "Escape") {
                setDraft(value ?? "");
                setInvalid(false);
                e.currentTarget.blur();
              }
            }}
          />
        </Tooltip>

        {isSet && (
          <Tooltip title="Reset to kiosk default">
            <ClearBtn onClick={() => onClear(fieldKey)}>
              <CloseCircleOutlined />
            </ClearBtn>
          </Tooltip>
        )}
      </SwatchGroup>

      <FieldMeta>
        <FieldLabel>{label}</FieldLabel>
        <FieldDescription title={description}>{description}</FieldDescription>
      </FieldMeta>
    </FieldRow>
  );
}

export default ColorField;
